import { useEffect, useRef, useState } from 'react';

// Loads heavy media only when the element nears the viewport. Geometry must be
// reserved by the caller (viewBox/aspect) so deferral never shifts layout.
// `priority` skips deferral for LCP content; missing IntersectionObserver
// falls back to immediate load.
export function useDeferredMedia<T extends HTMLElement>(priority = false) {
  const ref = useRef<T | null>(null);
  const [shouldLoad, setShouldLoad] = useState(priority);

  useEffect(() => {
    if (shouldLoad) return;
    if (priority) {
      setShouldLoad(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [priority, shouldLoad]);

  return { ref, shouldLoad };
}
